/**
 * The unapplied quiet-hours edit.
 *
 * Held by the screen rather than the card, because the error state replaces
 * everything under the header, the Delivery tab included.
 *
 * It re-syncs on the four saved values and the company, never on the settings
 * row as a whole: flipping any other switch replaces that row, and an edit
 * waiting for Apply must not go with it. As on the send limits, a rejected write
 * leaves the draft in place to retry.
 *
 * @link ./NotificationDeliveryCard for the fields it backs.
 */
import { useEffect, useState } from 'react';
import type { MasterSwitches } from './types';

export interface QuietHoursDraft {
  enabled: boolean;
  start: string;
  end: string;
  applyToCritical: boolean;
}

export interface UseQuietHoursDraftResult {
  draft: QuietHoursDraft;
  setDraft: (draft: QuietHoursDraft) => void;
  /** The draft differs from what the server holds. */
  changed: boolean;
  /** Enabled without both ends of the window, which the API refuses. */
  incomplete: boolean;
}

/**
 * @example
 * const quietHours = useQuietHoursDraft(setup.master, setup.catalogue?.companyId ?? null);
 * quietHours.setDraft({ ...quietHours.draft, start: '22:00' });
 *
 * @param master - the saved settings.
 * @param company - the company this screen is configuring. A draft typed for
 *   one company never carries over to another.
 * @returns the draft and what the card needs to know about it.
 */
export function useQuietHoursDraft(
  master: MasterSwitches,
  company: number | null,
): UseQuietHoursDraftResult {
  const {
    quietHoursEnabled: enabled,
    quietHoursStart: start,
    quietHoursEnd: end,
    quietHoursApplyToCritical: applyToCritical,
  } = master;
  const [draft, setDraft] = useState<QuietHoursDraft>({ enabled, start, end, applyToCritical });

  useEffect(() => {
    setDraft({ enabled, start, end, applyToCritical });
  }, [enabled, start, end, applyToCritical, company]);

  return {
    draft,
    setDraft,
    changed:
      draft.enabled !== enabled
      || draft.start !== start
      || draft.end !== end
      || draft.applyToCritical !== applyToCritical,
    incomplete: draft.enabled && (!draft.start || !draft.end),
  };
}
